import { motion } from "framer-motion";

import type { AgentGuard, AgentRun, AgentRunEdit } from "@/lib/api";
import { LEDDot } from "@/components/ui/LEDDot";
import { cn } from "@/lib/formatters";

// The social poller writes actions_json in its own shape (one entry per
// network), not the SEO AgentRunAction — so the panel branches on run.agent,
// which is authoritative even for a failed social run whose actions_json is [].
interface SocialDecision {
  red: string;
  formato: string | null;
  fit: number | null;
  score: number | null;
  decision: string;
  intentos: number;
}

const DECISION_HUE: Record<string, string> = {
  publicar: "text-success border-success/40 bg-success/5",
  suplente: "text-warn border-warn/40 bg-warn/5",
  saltado: "text-muted border-border bg-bg-elev/40",
  rechazar: "text-muted border-border bg-bg-elev/40",
};

interface RunDetailProps {
  run: AgentRun | null;
}

const ACTION_ICON: Record<string, string> = {
  applied: "✓",
  partial: "~",
  skipped: "–",
};

const ACTION_HUE: Record<string, string> = {
  applied: "text-success border-success/40 bg-success/5",
  partial: "text-warn border-warn/40 bg-warn/5",
  skipped: "text-muted border-border bg-bg-elev/40",
};

// A body edit is the one kind that always costs a human review, so it reads
// differently from the metadata kinds the guard is willing to merge on its own.
const EDIT_HUE: Record<AgentRunEdit["kind"], string> = {
  meta: "text-primary border-primary/40",
  canonical: "text-violet border-violet/40",
  robots: "text-violet border-violet/40",
  index: "text-primary border-primary/40",
  body: "text-magenta border-magenta/40",
};

export function RunDetail({ run }: RunDetailProps) {
  if (!run) {
    return (
      <div className="surface p-10 text-center">
        <p className="font-mono text-sm text-muted">$ select a run</p>
      </div>
    );
  }

  const guard = (run.guard_json ?? {}) as Partial<AgentGuard>;
  const actions = run.actions_json ?? [];
  const edits = run.edits_json ?? [];
  const social = run.agent === "social";

  return (
    <motion.div
      key={run.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* ── header ─────────────────────────────────────────────────── */}
      <div className="surface p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="path-chip">{run.mode || run.agent}</p>
            <h2 className="text-xl font-semibold mt-1">
              {run.week || (social ? "triage social" : "sin semana")}
            </h2>
            <p className="font-mono text-[11px] text-muted mt-1 truncate">
              run {run.run_id}
              {run.requested_by && ` · ${run.requested_by}`}
            </p>
          </div>
          <div className="text-right shrink-0">
            <LEDDot
              state={
                run.status === "success"
                  ? "ok"
                  : run.status === "failure"
                    ? "danger"
                    : run.status === "running"
                      ? "info"
                      : "warn"
              }
              label={run.status}
            />
            {run.auto_merge && (
              <p className="mt-2 font-mono text-[10px] uppercase text-success">auto-merged</p>
            )}
          </div>
        </div>

        {run.summary && (
          <p className="mt-3 pt-3 border-t border-border/40 text-sm text-muted/90 leading-relaxed whitespace-pre-line">
            {run.summary}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {run.pr_url && (
            <a
              href={run.pr_url}
              target="_blank"
              rel="noreferrer"
              className="px-2.5 py-1 rounded border border-success/50 bg-success/10 text-success font-mono text-xs hover:bg-success/20 transition-colors"
            >
              ver pull request ↗
            </a>
          )}
          {run.run_url && (
            <a
              href={run.run_url}
              target="_blank"
              rel="noreferrer"
              className="px-2.5 py-1 rounded border border-border text-muted font-mono text-xs hover:text-text transition-colors"
            >
              logs del workflow ↗
            </a>
          )}
        </div>
      </div>

      {/* ── guard verdict ──────────────────────────────────────────── */}
      {guard.ok !== undefined && (
        <div className="surface p-4">
          <div className="flex items-center justify-between">
            <p className="path-chip">guard</p>
            <span
              className={cn(
                "font-mono text-xs uppercase",
                guard.auto_merge ? "text-success" : "text-warn",
              )}
            >
              {guard.auto_merge ? "auto-merge" : "requiere review"}
            </span>
          </div>

          {(guard.changed_files ?? []).length > 0 && (
            <ul className="mt-3 space-y-1 font-mono text-[11px] text-muted">
              {guard.changed_files!.map((f) => (
                <li key={f} className="truncate">
                  {f}
                </li>
              ))}
            </ul>
          )}

          {(guard.blockers ?? []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-danger/30">
              <p className="font-mono text-[10px] uppercase text-danger">blockers</p>
              <ul className="mt-1 space-y-1 text-xs text-danger/90">
                {guard.blockers!.map((b) => (
                  <li key={b}>· {b}</li>
                ))}
              </ul>
            </div>
          )}

          {(guard.review_reasons ?? []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-warn/30">
              <p className="font-mono text-[10px] uppercase text-warn">por qué espera review</p>
              <ul className="mt-1 space-y-1 text-xs text-warn/90">
                {guard.review_reasons!.map((r) => (
                  <li key={r}>· {r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── actions ────────────────────────────────────────────────── */}
      {social ? (
        <div className="surface overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <p className="path-chip">decisiones por red</p>
          </div>
          {actions.length === 0 && (
            <p className="px-4 py-3 font-mono text-xs text-muted/70">
              sin decisiones registradas — la corrida no dejó artefacto
            </p>
          )}
          <ul className="divide-y divide-border/40">
            {(actions as unknown as SocialDecision[]).map((d) => (
              <li key={d.red} className="p-3 flex items-center gap-3">
                <span
                  className={cn(
                    "shrink-0 px-1.5 py-0.5 rounded-sm border font-mono text-[10px] uppercase",
                    DECISION_HUE[d.decision] ?? "text-muted border-border",
                  )}
                >
                  {d.decision}
                </span>
                <span className="text-sm text-text">{d.red}</span>
                {d.formato && (
                  <span className="font-mono text-[10px] text-muted/70">{d.formato}</span>
                )}
                <span className="ml-auto font-mono text-[10px] text-muted/70">
                  {d.score != null && `score ${d.score}`}
                  {d.fit != null && ` · fit ${d.fit}`}
                  {d.intentos > 0 && ` · ${d.intentos} int`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        actions.length > 0 && (
          <div className="surface overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <p className="path-chip">acciones</p>
            </div>
            <ul className="divide-y divide-border/40">
              {actions.map((a) => (
                <li key={a.index} className="p-4">
                  <div className="flex items-start gap-3">
                    <span
                      className={cn(
                        "shrink-0 flex items-center justify-center w-7 h-7 rounded border font-mono text-xs",
                        ACTION_HUE[a.status] ?? ACTION_HUE.skipped,
                      )}
                    >
                      {ACTION_ICON[a.status] ?? "?"}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-text leading-snug">
                        <span className="font-mono text-[10px] text-muted/70 mr-1.5">
                          P{a.priority} {a.type}
                        </span>
                        {a.title}
                      </p>
                      <p className="mt-1 text-xs text-muted/80 leading-relaxed">{a.note}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )
      )}

      {/* ── diff ───────────────────────────────────────────────────── */}
      {edits.length > 0 && (
        <div className="surface overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <p className="path-chip">cambios</p>
            <span className="font-mono text-xs text-muted">{edits.length}</span>
          </div>
          <ul className="divide-y divide-border/40">
            {edits.map((e, i) => (
              <li key={`${e.path}-${e.field}-${i}`} className="p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "px-1.5 py-0.5 rounded-sm border font-mono text-[10px] uppercase",
                      EDIT_HUE[e.kind] ?? "text-muted border-border",
                    )}
                  >
                    {e.kind}
                  </span>
                  <span className="font-mono text-[11px] text-muted truncate">{e.path}</span>
                  <span className="font-mono text-[11px] text-muted/60 ml-auto shrink-0">
                    {e.field}
                  </span>
                </div>
                <p className="font-mono text-[11px] text-danger/80 break-words">
                  − {e.before || "(vacío)"}
                </p>
                <p className="font-mono text-[11px] text-success/90 break-words">
                  + {e.after || "(vacío)"}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}
